import React from "react";
import "./dsstackscreen.css"
import {Paths} from "../../app/components/sidebar/Paths";
import {GradientHeaderComponent} from "../../app/components/gradientheader/GradientHeaderComponent";
import {ScreenComponent} from "../../app/components/screen/ScreenComponent";
import {H2SectionComponent, H3SectionComponent} from "../../app/components/section/SectionComponent";
import {AlertComponent} from "../../app/components/alert/AlertComponent";
import {AccordionComponent} from "../../app/components/accordion/AccordionComponent";
import {AccordionItemComponent} from "../../app/components/accordion/AccordionItemComponent";
import {
    AccordionHeaderTextComponent,
    AccordionHeaderTextTimeComplexityComponent
} from "../../app/components/accordion/AccordionHeaderTextComponent";
import {CarouselComponent} from "../../app/components/carousel/CarouselComponent";
import {CarouselItemComponent} from "../../app/components/carousel/CarouselItemComponent";
import {ProblemSetComponent} from "../components/problemset/ProblemSet";
import LinkComponent, {ALinkTarget} from "../../app/components/link/LinkComponent";
import {CodeComponent} from "../../app/components/code/CodeComponent";
import {FullCaptionComponent} from "../../app/components/caption/CaptionComponent";
import {ThemeColor} from "../../home/components/homecard/HomeCardComponent";
import StackImplementationArrayPushNeg96 from "./assets/implementation/array/StackImplementationArrayPushNeg96";
import StackImplementationArrayPush12 from "./assets/implementation/array/StackImplementationArrayPush12";
import StackImplementationArrayPush14 from "./assets/implementation/array/StackImplementationArrayPush14";
import StackImplementationArrayPop14 from "./assets/implementation/array/StackImplementationArrayPop14";
import StackImplementationArrayPushNeg4 from "./assets/implementation/array/StackImplementationArrayPushNeg4";
import StackImplementationArrayPush0 from "./assets/implementation/array/StackImplementationArrayPush0";
import StackImplementationArrayPush88 from "./assets/implementation/array/StackImplementationArrayPush88";
import StackImplementationLinkedListPushNeg96
    from "./assets/implementation/linkedlist/StackImplementationLinkedListPushNeg96";
import StackImplementationLinkedListPush12
    from "./assets/implementation/linkedlist/StackImplementationLinkedListPush12";
import StackImplementationLinkedListPush14
    from "./assets/implementation/linkedlist/StackImplementationLinkedListPush14";
import StackImplementationLinkedListPop14 from "./assets/implementation/linkedlist/StackImplementationLinkedListPop14";
import StackImplementationLinkedListPushNeg4
    from "./assets/implementation/linkedlist/StackImplementationLinkedListPushNeg4";
import StackImplementationLinkedListPop12 from "./assets/implementation/linkedlist/StackImplementationLinkedListPop12";
import StackImplementationLinkedListPush0 from "./assets/implementation/linkedlist/StackImplementationLinkedListPush0";
import StackPushPepper from "./assets/push/StackPushPepper";
import StackPushOrange from "./assets/push/StackPushOrange";
import StackPushBroccoli from "./assets/push/StackPushBroccoli";
import StackPushPepper2 from "./assets/push/StackPushPepper2";
import StackPopBroccoli from "./assets/pop/StackPopBroccoli";
import StackPopPepper from "./assets/pop/StackPopPepper";
import StackPopOrange from "./assets/pop/StackPopOrange";
import StackPopPepper2 from "./assets/pop/StackPopPepper2";
import StackPopNull from "./assets/pop/StackPopNull";

export default function DSStackScreen() {
    return (<ScreenComponent>
        <Header/>
        <Conceptualizing/>
        <Visualizing/>
        <TimeComplexity/>
        <ProblemSet/>
    </ScreenComponent>);
}

function Header() {
    return (<GradientHeaderComponent title={Paths.dataStructures.nested.stack.name}
                                     tagline={Paths.dataStructures.nested.stack.tagline}/>)
}

function Conceptualizing() {
    return (<H2SectionComponent header="Conceptualizing Stacks" removeTopPadding={true}>
        <ul>
            <li>A stack is as simple as its name. It's a neatly arranged pile of plates at a restaurant.</li>
            <li>The last plate on the pile - the one on top - is the next one picked up to be used. If you pulled from
                the bottom, you would risk toppling the stack over and breaking the ones on top.
            </li>
            <li>If a new plate comes along, it would get put on the top of the stack.</li>
            <li>You often hear stacks referred to as "Last in, first out" or "LIFO" for short. Which, as we can see,
                simply means the first element added to the stack, will be the last element removed from the stack.
                This is opposite to how <LinkComponent link={Paths.dataStructures.nested.queue.path}
                                                       target={ALinkTarget.internal}>queues</LinkComponent> work.
            </li>
            <li>We don't really care too much about what's in between the first and last spot.</li>
        </ul>
    </H2SectionComponent>)
}

function Visualizing() {
    return (<H2SectionComponent header="Visualizing Stacks">
        <AlertComponent color={ThemeColor.Secondary}>
            <p><strong>NOTE:</strong> A Stack is an <i>abstract</i> data structure. Unlike an Integer, which
                just <i>is</i> a 32-bit number, Stacks are really a structure with defined principles that can be
                implemented in many ways.</p>
        </AlertComponent>
        <Principles/>
        <Implementation/>
    </H2SectionComponent>)
}

function Principles() {
    return (<H3SectionComponent header="Prinicples of a Stack" removeTopPadding={true}>
        <ul>
            <li>Elements must be removed in the opposite order to which they were added, or pushed, onto the stack.</li>
            <li>A <code>push</code> function must be present that inserts an element at the top of the stack. This
                function may also be named <code>add</code> or <code>insertLast</code> depending on the language. The
                naming doesn't matter, so long as the functionality takes in an element and adds it to the end, or top,
                of the stack.
            </li>
            <li>A <code>pop</code> function must be present that removes an element from the top of the stack.
                This function may also be named <code>remove</code> or <code>removeLast</code> depending on the
                language. Again, the naming doesn't matter, so long as the functionality removes the last item on the
                stack.
            </li>
        </ul>
    </H3SectionComponent>)
}

function Implementation() {
    return (<H3SectionComponent header="Implementation of a Stack">
        <p>There are several ways to implement a stack. I will not cover all of them, but I will show a few
            examples just to highlight some differences. The exact syntax isn't as important as the foundational
            principles covered in the previous section.
        </p>
        <AccordionComponent>
            <StackImplementationArray/>
            <StackImplementationLinkedList/>
        </AccordionComponent>
    </H3SectionComponent>)
}

function StackImplementationArray() {
    const arrayType = "Array<Int>"
    return (<AccordionItemComponent header={<AccordionHeaderTextComponent title="Array" description="The basic way"/>}>

        <p>For example, if we wanted to keep a stack of numbers, we could use
            an <CodeComponent>{arrayType}</CodeComponent>.
        </p>
        <p>To push an Int onto our stack, we could simply do <CodeComponent>stack[stack.size] =
            value</CodeComponent>. This would ensure we are always adding our new value to the end of our array,
            which in this case is representing our stack. Because we know the index we want to set our value at,
            we would consider this an <CodeComponent>O(1)</CodeComponent> operation (assuming we are not having
            to resize our array).
        </p>
        <p>Next, we need to worry about our <CodeComponent>pop</CodeComponent> operation. We could do
            something like, <CodeComponent>stack.removeAtIndex(stack.size - 1)</CodeComponent>. As we saw
            with <LinkComponent link={Paths.dataStructures.nested.array}
                                target={ALinkTarget.internal}>arrays</LinkComponent>, this is
            an<CodeComponent>O(1)</CodeComponent> operation.
        </p>
        <p>Let's take a look at an example of a stack implementation using an array.</p>
        <CarouselComponent>
            <CarouselItemComponent><StackImplementationArrayPushNeg96/></CarouselItemComponent>
            <CarouselItemComponent><StackImplementationArrayPush12/></CarouselItemComponent>
            <CarouselItemComponent><StackImplementationArrayPush14/></CarouselItemComponent>
            <CarouselItemComponent><StackImplementationArrayPop14/></CarouselItemComponent>
            <CarouselItemComponent><StackImplementationArrayPushNeg4/></CarouselItemComponent>
            <CarouselItemComponent><StackImplementationArrayPush0/></CarouselItemComponent>
            <CarouselItemComponent><StackImplementationArrayPush88/></CarouselItemComponent>
        </CarouselComponent>
        <p>Here, we can see that this does add some overhead in situations where we need to double the size of our
            array if we ran out of spots.</p>
    </AccordionItemComponent>)
}

function StackImplementationLinkedList() {
    const linkedListType = "LinkedList<Int>"
    return (<AccordionItemComponent
        header={<AccordionHeaderTextComponent title="LinkedList" description="Fixing the overhead problem"/>}>
        <p>Similarly to what we saw in queues, we can get rid of some overhead of doubling the size of our array by
            utilizing another data structure called LinkedLists, or
            a <CodeComponent>{linkedListType}</CodeComponent> in this case. While we don't have to solve for quite
            as many problems as we do in implementing a queue, it does still help in resizing issues.
        </p>
        <p>If we keep track of the "bottom" and "top" indices, we can see how a LinkedList would be a great choice
            as we could use its <CodeComponent>head</CodeComponent> and <CodeComponent>tail</CodeComponent> pointers
            to keep track of which item we want to pop off from our stack, and where we would want to push onto our
            stack.
        </p>
        <p>We haven't covered LinkedLists yet, but the idea is that instead of moving to the next index + 1
            spot in the array to determine the next element, we would just use a "next" pointer at each spot
            that tells us which item comes after the current node. While I will cover this in more detail in a
            future course, it's worth calling out now as it solves the issue of needing to double the size of our
            array. Take a look at the example below.
            <CarouselComponent>
                <CarouselItemComponent><StackImplementationLinkedListPushNeg96/></CarouselItemComponent>
                <CarouselItemComponent><StackImplementationLinkedListPush12/></CarouselItemComponent>
                <CarouselItemComponent><StackImplementationLinkedListPush14/></CarouselItemComponent>
                <CarouselItemComponent><StackImplementationLinkedListPop14/></CarouselItemComponent>
                <CarouselItemComponent><StackImplementationLinkedListPop12/></CarouselItemComponent>
                <CarouselItemComponent><StackImplementationLinkedListPushNeg4/></CarouselItemComponent>
                <CarouselItemComponent><StackImplementationLinkedListPush0/></CarouselItemComponent>
            </CarouselComponent>
        </p>
    </AccordionItemComponent>)
}

function TimeComplexity() {
    return (<H2SectionComponent header="What is the time complexity?">
        <AccordionComponent>
            <PushTimeComplexity/>
            <PopTimeComplexity/>
            <PeekTimeComplexity/>
        </AccordionComponent>
    </H2SectionComponent>)
}

function PushTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (<AccordionItemComponent
        header={<AccordionHeaderTextTimeComplexityComponent title="Push" timeComplexity="O(1)"/>}>

        <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
            <CarouselItemComponent><StackPushPepper/></CarouselItemComponent>
            <CarouselItemComponent><StackPushOrange/></CarouselItemComponent>
            <CarouselItemComponent><StackPushPepper2/></CarouselItemComponent>
            <CarouselItemComponent><StackPushBroccoli/></CarouselItemComponent>
        </CarouselComponent>

        {activeIndex === 0 && <StackPushPepperFullCaption/>}
        {activeIndex === 1 && <StackPushOrangeFullCaption/>}
        {activeIndex === 2 && <StackPushPepper2FullCaption/>}
        {activeIndex === 3 && <StackPushBroccoliFullCaption/>}
    </AccordionItemComponent>)
}

function StackPushPepperFullCaption() {
    return (<FullCaptionComponent title="VALUE: Pepper">
        <p>Inserting a new element into a Stack is quite simple, especially when using LinkedLists as we don't have to
            worry about space allocation or time complexity worries.</p>
        <p>Just like in a cupboard, if a new plate wants to be stacked, it must go on the top of the pile. In this case,
            there is no pile yet, so our pepper plate just goes on the top, and only spot, in the stack.</p>
    </FullCaptionComponent>)
}

function StackPushOrangeFullCaption() {
    return (<FullCaptionComponent title="VALUE: Orange">
        <p>Again, all we must do is ensure the Orange plate is inserted on the top of our stack. So, the Orange plate
            sits on top of the Pepper plate.</p>
    </FullCaptionComponent>)
}

function StackPushPepper2FullCaption() {
    return (<FullCaptionComponent title="VALUE: Pepper">
        <p>There are no rules about duplicates. The only thing we must worry about is adding the plate on the top
            of the stack. So, as we can see, the new Pepper plate will sit on top of the previous Orange plate and we
            are good to go.</p>
    </FullCaptionComponent>)
}

function StackPushBroccoliFullCaption() {
    return (<FullCaptionComponent title="VALUE: Broccoli">
        <p>Again, all we must do is ensure the Broccoli plate is inserted on the top of our stack. So, the Broccoli
            plate sits on top of the Pepper plate.</p>
    </FullCaptionComponent>)
}

function PopTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (<AccordionItemComponent
        header={<AccordionHeaderTextTimeComplexityComponent title="Pop" timeComplexity="O(1)"/>}>

        <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
            <CarouselItemComponent><StackPopBroccoli/></CarouselItemComponent>
            <CarouselItemComponent><StackPopPepper/></CarouselItemComponent>
            <CarouselItemComponent><StackPopOrange/></CarouselItemComponent>
            <CarouselItemComponent><StackPopPepper2/></CarouselItemComponent>
            <CarouselItemComponent><StackPopNull/></CarouselItemComponent>
        </CarouselComponent>

        {activeIndex === 0 && <StackPopBroccoliFullCaption/>}
        {activeIndex === 1 && <StackPopPepperFullCaption/>}
        {activeIndex === 2 && <StackPopOrangeFullCaption/>}
        {activeIndex === 3 && <StackPopPepper2FullCaption/>}
        {activeIndex === 4 && <StackPopNullFullCaption/>}
    </AccordionItemComponent>)
}

function StackPopBroccoliFullCaption() {
    return (<FullCaptionComponent title="VALUE: Broccoli">
        <p>Removing an element from a Stack is quite simple, especially when using LinkedLists as we don't have to
            worry about space allocation or time complexity worries.</p>
        <p>Just like in a cupboard, the plate on top of the stack - Broccoli, in this case - will be the next one
            removed from our stack. We remove the Broccoli plate from our stack.</p>
    </FullCaptionComponent>)
}

function StackPopPepperFullCaption() {
    return (<FullCaptionComponent title="VALUE: Pepper">
        <p>Just like we did with our previous value, we are going to continue to remove the top plate in the stack.
            Here, we will remove the Pepper plate.</p>
        <p>Even though there is another Pepper plate in the stack, we only care about the very first top in our stack,
            or the last instance of the Pepper plate in this case.</p>
    </FullCaptionComponent>)
}

function StackPopOrangeFullCaption() {
    return (<FullCaptionComponent title="VALUE: Orange">
        <p>Just like we did with our previous value, we are going to continue to remove the top plate in the stack.
            Here, we will remove the Orange plate.</p>
    </FullCaptionComponent>)
}

function StackPopPepper2FullCaption() {
    return (<FullCaptionComponent title="VALUE: Pepper">
        <p>Just like we did with our previous value, we are going to continue to remove the top plate in the stack.
            Here, we will remove the Pepper plate.</p>
    </FullCaptionComponent>)
}

function StackPopNullFullCaption() {
    return (<FullCaptionComponent title="VALUE: null">
        <p>You should always prevent edge cases of a user or program trying to remove an element from a stack that
            doesn't exist.</p>
        <p>If a user were to try to call <CodeComponent>pop</CodeComponent> with no plates left in the stack, we should
            first check that there <i>is</i> a plate in the stack first before we try popping it off, or removing it,
            from the stack.</p>
    </FullCaptionComponent>)
}

function PeekTimeComplexity() {
    return (<AccordionItemComponent
        header={<AccordionHeaderTextTimeComplexityComponent title="Peek" timeComplexity="O(1)"/>}>
        <ul>
            <li>In some use cases, it may be helpful to know what is the last element in our stack.</li>
            <li>We may only want to remove an item from the stack if it's reached a certain threshold. In this case, we
                would need to know what that very first item is in our stack before removing it.
            </li>
            <li>While we could use <CodeComponent>pop</CodeComponent> to get the value returned to us, it will also
                remove it from the stack. While we could just add it back to the stack, it does add an extra
                complication to keep track of.
            </li>
            <li>Therefore, we introduce a <CodeComponent>peek</CodeComponent> function which just simply returns the
                value of the last item in the stack, but doesn't <i>remove</i> that item from the stack.
            </li>
        </ul>
    </AccordionItemComponent>)
}

function ProblemSet() {
    return (<ProblemSetComponent dataStructure={Paths.dataStructures.nested.stack.name}>
        <BasicProblems/>
        <AdvancedProblems/>
    </ProblemSetComponent>)
}

function BasicProblems() {
    return (<H3SectionComponent header="Basic Problems" removeTopPadding={true}>
        <ul>
            <li><LinkComponent link="https://leetcode.com/problems/valid-parentheses/description/">Valid
                Parenthesis</LinkComponent> (Parenthesis pattern)
            </li>
            <li><LinkComponent link="https://leetcode.com/problems/evaluate-reverse-polish-notation/description/">Evaluate
                Reverse Polish Notation</LinkComponent> (Calculator pattern)
            </li>
            <li><LinkComponent link="https://leetcode.com/problems/next-greater-element-i/description/">Next Greater
                Element 1</LinkComponent> (Monotonic Stack pattern)
            </li>
        </ul>
    </H3SectionComponent>)
}

function AdvancedProblems() {
    return (<H3SectionComponent header="Advanced Problems">
        <ul>
            <li><LinkComponent link="https://leetcode.com/problems/largest-rectangle-in-histogram/description/">Largest
                Rectangle in Histogram</LinkComponent> One of my favorite problems, it is a beautiful problem that is
                trickier than it first appears.
            </li>
            <li><LinkComponent link="https://leetcode.com/problems/basic-calculator-ii/description/">Basic Calculator
                II</LinkComponent> (Calculator pattern)
            </li>
            <li>Will update when we've covered DFS.</li>
        </ul>
    </H3SectionComponent>)
}
